import React, { useState, useEffect, ExternalLink } from 'react';
import classNames from 'classnames';
import {Row, Col, Card} from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { 
  dataApiServerIsRunning, 
  dataApiServerVersion, 
  dataApiTenantDbIsOk, 
  dataApiDatawarehouseIsOk, 
  checkOllamaConnection, 
  badDogApiServerIsRunning, 
  badDogApiServerVersion, 
  dataApiMaterializedViewsAreOk, 
  buildUrl, 
  buildAuthUrl,
  fetchWithAccessToken,
  fetchWithToken
} from '../services/HttpService';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { useIsAuthenticated } from '@azure/msal-react';
import AuthenticationStatus from '../components/telescope/AuthenticationStatus';
import { getAccessToken } from '../utils/tokenUtils';

const styles = {
  container: {
    textAlign: 'center',
    marginTop: '20%',
    background: 'linear-gradient(90deg, rgba(83,108,48,1) 0%, rgba(45,46,70,1) 25%, rgba(45,46,70,1) 100%)',
    color: 'white',
    padding: '20px',
    borderRadius: '10px',
  },
  colorblack: {
    color: 'black',
  },
  colorwhite: {
    color: 'white',
  },
 apiversion: {
    fontSize: '.5em',
    fontFamily: "Courier New",
    color: 'green'
  },
};

const health = () => {

  // MS-Entra / MSAL
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const isAuthenticated = useIsAuthenticated();

  const [messageDataApi, setMessageDataApi] = useState('Loading...');
  const [versionDataApi, setVersionDataApi] = useState();
  const [messageDataApiDW, setMessageDataApiDW] = useState('Loading...');
  const [messageDataApiTenantDb, setMessageDataApiTenantDb] = useState('Loading...');
  const [messageDataApiOllama, setMessageDataApiOllama] = useState('Loading...');
  const [messageMaterializedViews, setMessageMaterializedViews] = useState('Loading...');

  const [messageBadDogApi, setMessageBadDogApi] = useState('Loading...');
  const [versionBadDogApi, setVersionBadDogApi] = useState();

  const [dataApiIsRunning, setDataApiIsRunning] = useState(false);
  const [dataApiDWIsOk, setDataApiDWIsOk] = useState(false); 
  const [dataApiTtDbIsOk, setDataApiTDbIsOk] = useState(false); 
  const [dataApiOllamaIsOk, setDataApiOllamaIsOk] = useState(false); 
  const [statusMaterializedViews, setStatusMaterializedViews] = useState({});

  const [badDogApiIsRunning, setBadDogApiIsRunning] = useState(false);

  // Error state tracking for each ping type
  const [errorDataApi, setErrorDataApi] = useState(null);
  const [errorDataApiDW, setErrorDataApiDW] = useState(null);
  const [errorDataApiTenantDb, setErrorDataApiTenantDb] = useState(null);
  const [errorDataApiOllama, setErrorDataApiOllama] = useState(null);
  const [errorBadDogApi, setErrorBadDogApi] = useState(null);
  const [errorMaterializedViews, setErrorMaterializedViews] = useState(null);

  // Custom error handler that prevents redirects and extracts error info immediately
  const handleHealthCheckError = async (response) => {
    // Don't redirect, but DO extract the error message before the body is consumed
    console.log(`Health check received error response: ${response.status} ${response.statusText}`);
    
    // Clone the response so we can read the body without consuming it for other handlers
    const clonedResponse = response.clone();
    
    let errorMessage = null;
    try {
      // Try to extract error message from response body
      const responseBody = await clonedResponse.json();
      errorMessage = responseBody?.message || responseBody?.msg || responseBody?.error || responseBody?.detail;
      console.log(`Extracted error message from ${response.status} response:`, errorMessage);
    } catch (parseError) {
      // If we can't parse JSON, try to get text
      try {
        const textBody = await response.clone().text();
        if (textBody && textBody.length < 200) {
          errorMessage = textBody;
        }
      } catch (textError) {
        console.error("Could not parse error response body:", textError);
      }
    }
    
    // Attach the extracted error message to the response object for later use
    response.errorMessage = errorMessage;
    
    return response;
  };

  // Custom ping function for TenantDB that bypasses redirect logic
  const pingTenantDbWithErrorHandling = async () => {
    try {
      const url = buildUrl("ping/tenantdb");
      
      // Call fetchWithAccessToken with custom error handler to prevent redirects
      const response = await fetchWithAccessToken(url, {}, handleHealthCheckError);
      
      if (!response) {
        throw new Error("No response received from server");
      }
      
      // if (response.ok && response.statusText === "OK") {
      if (response.ok) {
        return true;
      }
      
      // Handle error response - use the errorMessage extracted by handleHealthCheckError
      let errorMessage = `HTTP ${response.status}`;
      
      // Use the error message that was already extracted and attached to the response
      if (response.errorMessage) {
        errorMessage = `${errorMessage}: ${response.errorMessage}`;
      } else {
        errorMessage = `${errorMessage} ${response.statusText}`;
      }
      
      // Provide specific context for common error codes
      if (response.status === 401 || response.status === 403) {
        throw new Error(`Authentication error - ${errorMessage}`);
      } else if (response.status === 500) {
        throw new Error(`Server error - ${errorMessage}`);
      } else if (response.status === 503) {
        throw new Error(`Service unavailable - ${errorMessage}`);
      } else {
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error("pingTenantDbWithErrorHandling: ", error);
      throw error;
    }
  };

  // Custom ping function for Materialized Views with error handling
  const pingMaterializedViewsWithErrorHandling = async () => {
    try {
      const url = buildUrl("health/materialized-views");
      
      const response = await fetchWithAccessToken(url, {}, handleHealthCheckError);
      
      if (!response || !response.ok) {
        let errorMessage = `HTTP ${response?.status || 'unknown'}`;
        
        // Use the error message that was already extracted by handleHealthCheckError
        if (response?.errorMessage) {
          errorMessage = `${errorMessage}: ${response.errorMessage}`;
        } else if (response?.statusText) {
          errorMessage = `${errorMessage} ${response.statusText}`;
        }
        
        return { success: false, error: errorMessage, data: null };
      }
      
      const responseBody = await response.json();
      
      return {
        success: true,
        data: responseBody,
        status: response.status,
        statusText: response.statusText
      };
    } catch (error) {
      console.error("pingMaterializedViewsWithErrorHandling: ", error);
      return { 
        success: false, 
        error: error.message || "Unknown error occurred",
        data: null 
      };
    }
  };


  useEffect(() => {

      const checkDataApiMaterializedViewsAreOk = async () => {
        try {
          const response = await pingMaterializedViewsWithErrorHandling();
          const areOk = response.success 
            && response.data?.mv_contract_search
            && response.data?.mv_supplier_locations
            && response.data?.mv_supplier_profile;

          console.log("Materialized Views are OK: " + areOk);

          setMessageMaterializedViews(areOk ? "OK" : "NOT OK");
          // response.success can be true with individual views not-ok; only surface an error banner for actual call failures
          setErrorMaterializedViews(response.success ? null : (response.error || "Failed to check materialized views"));
          return response;
        } catch (error) {
          console.error("Error checking materialized views:", error);
          setMessageMaterializedViews("NOT OK");
          setErrorMaterializedViews(error.message || "Failed to check materialized views");
          return { success: false, error: error.message, data: null };
        }
      };

      // call PING endpoint on the DataAPI server
      const pingDataApi = async () => {
        try {
          const isRunning = await dataApiServerIsRunning();
          setDataApiIsRunning(isRunning);
          setMessageDataApi(isRunning ? "RUNNING" : "NOT RUNNING");

          const version = await dataApiServerVersion();
          setVersionDataApi(version);
          setErrorDataApi(null);
        } catch (error) {
          console.error("Error pinging DataAPI:", error);
          setErrorDataApi(error.message || "Failed to connect to DataAPI");
          setMessageDataApi("ERROR");
          setDataApiIsRunning(false);
        }
      };

      // call PING Datawarehouse endpoint on the DataAPI server
      const pingDataApiDW = async () => {
        try {
          const isOk = await dataApiDatawarehouseIsOk();
          setDataApiDWIsOk(isOk);
          setMessageDataApiDW(isOk ? "OK" : "NOT OK");
          setErrorDataApiDW(null);
          
          if (isOk && isAuthenticated) {
            const response_mv  = await checkDataApiMaterializedViewsAreOk();
            setStatusMaterializedViews(response_mv);
          }
        } catch (error) {
          console.error("Error pinging DataAPI Datawarehouse:", error);
          setErrorDataApiDW(error.message || "Failed to check Datawarehouse connection");
          setMessageDataApiDW("ERROR");
          setDataApiDWIsOk(false);
        }
      };

      // call PING Tenant DB endpoint on the DataAPI server
      const pingDataApiTenantDb = async () => {
        // Only ping tenant DB if user is authenticated
        if (!isAuthenticated) {
          setMessageDataApiTenantDb("NOT AUTHENTICATED");
          setDataApiTDbIsOk(false);
          setErrorDataApiTenantDb(null);
          return;
        }

        try {
          const isOk = await pingTenantDbWithErrorHandling();
          setDataApiTDbIsOk(isOk);
          setMessageDataApiTenantDb(isOk ? "OK" : "NOT OK");
          setErrorDataApiTenantDb(null);
        } catch (error) {
          console.error("Error pinging DataAPI TenantDB:", error);
          setErrorDataApiTenantDb(error.message || "Failed to check TenantDB connection");
          setMessageDataApiTenantDb("ERROR");
          setDataApiTDbIsOk(false);
        }
      };

      // call PING Ollama endpoint on the DataAPI server
      const pingDataApiOllama = async () => {
        try {
          console.log("pingDataApiOllama: About to call checkOllamaConnection");
          const isOk = await checkOllamaConnection();
          console.log("pingDataApiOllama: Ollama result =", isOk);
          setDataApiOllamaIsOk(isOk);
          setMessageDataApiOllama(isOk ? "OK" : "NOT OK");
          setErrorDataApiOllama(null);
        } catch (error) {
          console.error("Error checking Ollama connection:", error);
          setErrorDataApiOllama(error.message || "Failed to check Ollama connection");
          setMessageDataApiOllama("ERROR");
          setDataApiOllamaIsOk(false);
        }
      };

      // call PING endpoint on the BADDOG server
      const pingBadDogApi = async () => {
        try {
          const isRunning = await badDogApiServerIsRunning();
          setBadDogApiIsRunning(isRunning);
          setMessageBadDogApi(isRunning ? "RUNNING" : "NOT RUNNING");

          const version = await badDogApiServerVersion();
          setVersionBadDogApi(version);
          setErrorBadDogApi(null);
        } catch (error) {
          console.error("Error pinging BadDogAPI:", error);
          setErrorBadDogApi(error.message || "Failed to connect to BadDog API");
          setMessageBadDogApi("ERROR");
          setBadDogApiIsRunning(false);
        }
      };

      // Execute all pings independently
      pingDataApi();
      pingDataApiDW();
      pingDataApiTenantDb();
      pingDataApiOllama();
      pingBadDogApi();

  }, [isAuthenticated]);

  return (
    <>
      <Row>
        <Col lg={3}></Col>
        <Col lg={6}>
            <br />
            <h1>Health Check</h1>
            
            <br />
        </Col>
      </Row>
      <Row>

        <Col lg={3}></Col>

        <Col lg={6}>

            <Card className="mb-5">

              <Card.Header className="card-header-action bg-secondary-subtle">
                  <a href={buildUrl("ping")} >

                    <h3 className="text-secondary">
                        DataAPI
                    </h3>
                    <pre>{buildUrl("ping")}</pre>
                    <p>
                      <pre>{versionDataApi}</pre>
                    </p>

                  </a>

              </Card.Header>

              <Card.Body className="justify-content-start">
                  { messageDataApi === "RUNNING" ? (
                      <>
                      <div>
                          <h4>
                            <span style={{ color: 'green', marginRight: '10px' }}>✔</span>
                            DataAPI is {messageDataApi} &nbsp;                          
                            <small className='text-end' style={styles.apiversion}>{versionDataApi}</small>
                          </h4>
                      </div>
                        </>
                    ) : (
                      <>
                        <h4 className='text-danger'>
                          <span style={{ color: 'red', marginRight: '10px' }}>X</span>
                          DataAPI is NOT running
                        </h4>
                        {errorDataApi && (
                          <div className="alert alert-danger mt-2" style={{ marginLeft: '30px', fontSize: '0.9em' }}>
                            <strong>Error:</strong> {errorDataApi}
                          </div>
                        )}
                      </>
                    )
                  }
                  { messageDataApiDW === "OK" ? (
                      <div style={{ color: 'green', marginLeft: '30px' }}>
                        <h5 >
                          <span style={{ color: 'green', marginRight: '10px' }}>✔</span>
                          <small>Datawarehouse DB connection is {messageDataApiDW}</small>
                        </h5>
                        {/* Render Materialized Views Status */}
                        <h6>
                          <span style={{ color: messageMaterializedViews === "OK" ? 'green' : 'red', marginRight: '10px' }}>
                            {messageMaterializedViews === "OK" ? '✔' : 'X'}
                          </span>
                          <small>Materialized Views: {messageMaterializedViews}</small>
                        </h6>
                        {statusMaterializedViews.data && Object.entries(statusMaterializedViews.data).map(([key, value]) => {
                          const isOk = Boolean(value);
                          const statusText = isOk ? "OK" : "NOT OK";
                          return (
                            <div key={key} style={{ marginLeft: '20px', marginTop: '5px' }}>
                              <h6 style={{ color: isOk ? 'green' : 'red' }}>
                                <span style={{ color: isOk ? 'green' : 'red', marginRight: '10px' }}>
                                  {isOk ? '✔' : 'X'}
                                </span>
                                <small>{key}: {statusText}</small>
                              </h6>
                            </div>
                          );
                        })}
                        {errorMaterializedViews && (
                          <div className="alert alert-warning mt-2" style={{ marginLeft: '20px', fontSize: '0.85em' }}>
                            <strong>Warning:</strong> {errorMaterializedViews}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div style={{ marginLeft: '30px' }}>
                          <h5 className='text-danger'>
                            <span style={{ color: 'red', marginRight: '10px' }}>X</span>
                            <small>Datawarehouse DB connection is {messageDataApiDW}</small>
                          </h5>
                          {errorDataApiDW && (
                            <div className="alert alert-danger mt-2" style={{ fontSize: '0.9em' }}>
                              <strong>Error:</strong> {errorDataApiDW}
                            </div>
                          )}
                        </div>
                      </>
                    )
                  }
                  { messageDataApiTenantDb === "OK" ? (
                      <div style={{ color: 'green', marginLeft: '30px' }}>
                        <h5>
                          <span style={{ color: 'green', marginRight: '10px' }}>✔</span>
                          <small>Tenant DB connection is {messageDataApiTenantDb}</small>
                        </h5>
                      </div>
                    ) : messageDataApiTenantDb === "NOT AUTHENTICATED" ? (
                      <>
                        <div style={{ marginLeft: '30px' }}>
                          <h5 className='text-warning'>
                            <span style={{ color: 'orange', marginRight: '10px' }}>ⓘ</span>
                            <small>Tenant DB: Not checked (authentication required)</small>
                          </h5>
                          <div className="alert alert-info mt-2" style={{ fontSize: '0.85em' }}>
                            <strong>Info:</strong> TenantDB ping requires authentication. Please sign in to check this connection.
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ marginLeft: '30px' }}>
                          <h5 className='text-danger'>
                              <span style={{ color: 'red', marginRight: '10px' }}>X</span>
                              <small>Tenant DB connection is {messageDataApiTenantDb}</small>
                          </h5>
                          {errorDataApiTenantDb && (
                            <div className="alert alert-danger mt-2" style={{ fontSize: '0.9em' }}>
                              <strong>Error:</strong> {errorDataApiTenantDb}
                            </div>
                          )}
                        </div>
                      </>
                    )
                  }
                  { messageDataApiOllama === "OK" ? (
                      <div style={{ color: 'green', marginLeft: '30px' }}>
                        <h5>
                          <span style={{ color: 'green', marginRight: '10px' }}>✔</span>
                          <small>Ollama connection is {messageDataApiOllama}</small>
                        </h5>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginLeft: '30px' }}>
                          <h5 className='text-danger'>
                            <span style={{ color: 'red', marginRight: '10px' }}>X</span>
                            <small>Ollama connection is {messageDataApiOllama}</small>
                          </h5>
                          {errorDataApiOllama && (
                            <div className="alert alert-danger mt-2" style={{ fontSize: '0.9em' }}>
                              <strong>Error:</strong> {errorDataApiOllama}
                            </div>
                          )}
                        </div>
                      </>
                    )
                  }
              </Card.Body>

            </Card>
          
            <Card className="mb-5">

              <Card.Header className="card-header-action bg-secondary-subtle">
                  <a href={buildAuthUrl("ping")} >
                      <h3 className="text-secondary">
                          BadDog API
                      </h3>
                      <pre>{buildAuthUrl("ping")}</pre>
                      <p>
                        <pre>{versionBadDogApi}</pre>
                      </p>
                  </a>
              </Card.Header>

              <Card.Body className="justify-content-start">
                  { messageBadDogApi === "RUNNING" ? (
                      <>
                        <h4>
                          <span style={{ color: 'green', marginRight: '10px' }}>✔</span>
                          BADDOG API is {messageBadDogApi} &nbsp;
                          <small className='text-end' style={styles.apiversion}>{versionBadDogApi}</small>
                        </h4>
                      </>
                    ) : (
                      <>
                        <h4 className='text-danger'>
                          <span style={{ marginRight: '10px' }}>X</span>
                          BADDOG API is NOT running
                        </h4>
                        {errorBadDogApi && (
                          <div className="alert alert-danger mt-2" style={{ marginLeft: '30px', fontSize: '0.9em' }}>
                            <strong>Error:</strong> {errorBadDogApi}
                          </div>
                        )}
                      </>
                    )
                  }
              </Card.Body>

            </Card>

            <AuthenticationStatus
              isAuthenticated={isAuthenticated}
              email={activeAccount?.username}
              name={activeAccount?.name}
              accountId={activeAccount?.localAccountId}
              environment={activeAccount?.environment}
            />
        </Col>
      </Row>
    </>
  );
};

health.propTypes = {
  message: PropTypes.string,
};


health.defaultProps = {
  message: '/health -- CHECK SETTINGS and CONNECTIVITY',
};

export default health;